import { Component, inject, OnInit, input } from '@angular/core';
import { Router } from '@angular/router';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import dayjs from 'dayjs';
import { copyToClipboard, SafePipe, FlIconComponent } from '@common';
import { SharedService } from '@shared/service/shared.service';
import { FmtFileSizePipe } from '@shared/component/attachment-list.component';
import { Notice, NoticeContentType, NoticeItemKind, NoticeMediaItem, NoticeStatus } from '../../message.api.service';
import { NoticeContentPipe, NoticeItemMetaPipe, NoticeView } from '../../message.pipe';
import { TabsKeyEnum } from '@app/task/task-analysis/components/task-analysis-chart/task-analysis-chart.component';

/**
 * 单个通知组件（纯展示 + 按钮点击，内部按 extraType 处理）
 *
 * 领域模型见 message/README.md：
 * - 接收完整 Notice，内部解析 content（文本/视频转码/反馈/下载导出），拼标签前缀
 * - 标签映射：extraType → 【标签】（4-9 统一简写"转码"）
 * - 视频转码(5/6/9)：content JSON 数组 {name,url,cover}[]，cover 存在渲染缩略图+播放，null 退化为文件名链接
 * - 反馈(11)：content JSON {firstName,content,photos[],...}，渲染学生名+反馈文本+图片+回复按钮
 * - 导出/下载(10)：content 为 safe HTML 链接（<a download>），直接渲染
 * - 云平台通知(3)：图谱生成等 content 为 safe HTML 链接（<a href>），直接渲染
 * - 报表导出(12)/成绩下载(13)：content 为 safe HTML 链接，直接渲染
 */
@Component({
  selector: 'fl-notice',
  templateUrl: './notice.component.html',
  styleUrls: ['./notice.component.scss'],
  standalone: true,
  imports: [
    NzImageModule,
    NzButtonModule,
    NzIconModule,
    SafePipe,
    FlIconComponent,
    NoticeContentPipe,
    NoticeItemMetaPipe,
    FmtFileSizePipe,
  ],
})
export class NoticeComponent implements OnInit {
  private router = inject(Router);
  private sharedSrv = inject(SharedService);
  private message = inject(NzMessageService);

  /** 单条通知 */
  readonly notice = input<Notice>(undefined);

  /** 模板里按枚举判断成功/失败配色 */
  protected readonly NoticeStatus = NoticeStatus;

  /** 视频转码类 extraType（5/6/9）：content 为 {name,url,cover}[] */
  private static readonly VIDEO_EXTRA_TYPES = [5, 6, 9];

  /** 反馈类 extraType */
  private static readonly DISCUSS_EXTRA_TYPE = 11;

  /** 导出/下载类 extraType：content 为 HTML 链接 */
  private static readonly DOWNLOAD_EXTRA_TYPE = 10;

  /** 云平台通知类 extraType：图谱生成等 content 为 HTML 链接 */
  private static readonly PLATFORM_EXTRA_TYPE = 3;

  /** 报表导出/成绩下载类 extraType：content 为 HTML 链接 */
  private static readonly REPORT_EXTRA_TYPES = [12, 13];

  /** 判断是否为导入报告结构：对象且含 success/error 键（导入任务数据等） */
  private static isImportReport(parsed: unknown): boolean {
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
    return 'success' in parsed || 'error' in parsed;
  }

  /** 把导入报告的键值对（文件名 → 文案）转为条目数组 */
  private static toImportEntries(map: unknown): NoticeImportEntry[] {
    if (!map || typeof map !== 'object' || Array.isArray(map)) return [];
    return Object.entries(map as Record<string, unknown>).map(([name, message]) => ({
      name,
      message: String(message),
    }));
  }

  /** extraType → 标签前缀（4-9 统一"转码"） */
  private static readonly EXTRA_TAG_MAP: Record<number, string> = {
    0: '新任务',
    1: '消息回复',
    2: '试卷被阅',
    3: '云平台通知',
    4: '转码',
    5: '转码',
    6: '转码',
    7: '转码',
    8: '转码',
    9: '转码',
    10: '下载',
    11: '任务反馈',
  };

  /** 解析后的内容形态（由 notice 初始化时计算） */
  videoList: NoticeVideo[] = [];
  discussContent: NoticeDiscuss | null = null;
  textContent = '';
  htmlContent = '';
  /** 导入报告：成功条目（文件名 + 结果文案） */
  importSuccess: NoticeImportEntry[] = [];
  /** 导入报告：失败条目（文件名 + 错误文案） */
  importError: NoticeImportEntry[] = [];

  get tagText() {
    const tag = NoticeComponent.EXTRA_TAG_MAP[this.notice()?.extraType];
    return tag ? `【${tag}】` : '';
  }

  ngOnInit() {
    this.parseContent();
  }

  /** 按 extraType 解析 content */
  private parseContent() {
    const notice = this.notice();
    if (!notice) return;

    const raw = notice.content;
    let parsed: any;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }

    if (NoticeComponent.VIDEO_EXTRA_TYPES.includes(notice.extraType) && Array.isArray(parsed)) {
      this.videoList = parsed.filter(v => v && v.url);
    } else if (notice.extraType === NoticeComponent.DISCUSS_EXTRA_TYPE && parsed && typeof parsed === 'object') {
      this.discussContent = parsed;
    } else if (
      notice.extraType === NoticeComponent.DOWNLOAD_EXTRA_TYPE ||
      notice.extraType === NoticeComponent.PLATFORM_EXTRA_TYPE ||
      NoticeComponent.REPORT_EXTRA_TYPES.includes(notice.extraType)
    ) {
      if (NoticeComponent.isImportReport(parsed)) {
        // 导入报告结构：success/error 键值对（文件名 → 结果/错误文案），逐条解析
        this.importSuccess = NoticeComponent.toImportEntries((parsed as any).success);
        this.importError = NoticeComponent.toImportEntries((parsed as any).error);
      } else if (parsed) {
        // 键值对 JSON 结构本轮不解析，统一按文本整块展示（待服务端改结构）
        this.textContent = raw;
      } else {
        // 下载/云平台通知/报表导出：content 为 safe HTML 链接字符串，直接渲染
        this.htmlContent = raw;
      }
    } else {
      this.textContent = raw;
    }
  }

  /** 是否有操作按钮 */
  get showReply() {
    return !!this.discussContent;
  }

  /** 复制错误反馈模板里的「功能」名称（1557 产品规范） */
  private static readonly FUNCTION_NAMES: Record<NoticeContentType, string> = {
    [NoticeContentType.Booklet]: '错题本导出',
    [NoticeContentType.Graph]: '图谱生成',
    [NoticeContentType.Exam]: '成绩下载',
    [NoticeContentType.Transcode]: '视频转码',
    [NoticeContentType.Correction]: '题目纠错',
    [NoticeContentType.Edoc]: '录入工单',
    [NoticeContentType.EdocAssign]: '录入工单',
    [NoticeContentType.EdocComplete]: '录入工单',
    [NoticeContentType.Import]: '成绩导入',
  };

  /** 结构化消息：下载产物（文件地址不能走路由跳转，App 内交给 webview 接管下载） */
  onDownload(url: string) {
    if (!url) return;
    window.open(url, this.sharedSrv.isApp ? '_self' : '_blank');
  }

  /** 结构化消息：点击产物条目，视频打开播放器，附件打开链接 */
  onOpenItem(item: NoticeMediaItem) {
    if (!item.url) return;
    if (item.kind === NoticeItemKind.Video) {
      this.playVideo(item);
    } else {
      window.open(item.url, this.sharedSrv.isApp ? '_self' : '_blank');
    }
  }

  /**
   * 复制错误反馈模板（1557 产品规范）
   *
   * 只允许在 traceback 存在时触发：无 traceback 的失败是数据/参数问题，不提供复制，
   * 避免用户把无定位价值的信息发给管理员。
   */
  onCopyError(view: NoticeView) {
    if (!view.traceback) return;

    const [errorType, errorInfo] = NoticeComponent.splitError(view.errorMessage);
    // 内容行首行即所选范围；失败场景 detail 可能多行（后续为引导语），复制模板只取范围
    const scope = (view.detail.split('\n')[0] ?? '').replace(/^所选范围[:：]\s*/, '');
    const user = this.sharedSrv.user;
    const lines = [
      '—— 系统消息错误反馈 ——',
      `发生时间：${dayjs(this.notice()?.createAt).format('YYYY-MM-DD HH:mm')}`,
      `功能：${NoticeComponent.FUNCTION_NAMES[view.type] ?? this.notice()?.title}`,
      `所选范围：${scope}`,
      user ? `用户：${user.first_name || user.username}（ID ${user.user_id}）` : '',
      `异常类型：${errorType}`,
      `错误信息：${errorInfo}`,
      '堆栈：',
      view.traceback,
      '—— 以上信息请一并发送给管理员 ——',
    ].filter(Boolean);

    copyToClipboard(lines.join('\n'))
      .then(() => this.message.success('复制成功'))
      .catch(() => this.message.error('复制失败，请重试'));
  }

  /** 拆分「IndexError: list index out of range」为异常类型与错误信息 */
  private static splitError(message: string): [string, string] {
    const match = message.match(/^([^:：]+)[:：]\s*(.*)$/);
    return match ? [match[1], match[2]] : ['', message];
  }

  /** 播放转码视频 */
  playVideo(video: NoticeVideo) {
    this.sharedSrv.showVideo('转码视频', video.cover, video.url, 0);
  }

  /** 反馈点击回复，跳转任务页 */
  onReply() {
    const c = this.discussContent;
    if (!c) return;
    const _taskId = c.originTaskId ? c.originTaskId : c.taskId;
    this.router.navigate([`task/${_taskId}/analysis`], {
      queryParams: {
        taskTitle: c.taskTitle,
        classId: c.classId,
        className: c.className,
        taskType: c.taskType,
        questionId: c.questionId,
        activeChatTab: c.questionId ? TabsKeyEnum.Question : TabsKeyEnum.Feedback,
      },
    });
  }
}

/** 视频转码项 */
export interface NoticeVideo {
  name: string;
  url: string;
  cover?: string | null;
}

/** 反馈通知的 task 跳转参数字段 */
export interface NoticeDiscuss {
  firstName: string;
  content: string;
  photos?: string[];
  taskId?: number;
  originTaskId?: number;
  taskTitle?: string;
  classId?: number;
  className?: string;
  taskType?: number | string;
  questionId?: number;
}

/** 导入报告条目（文件名 + 结果/错误文案） */
export interface NoticeImportEntry {
  name: string;
  message: string;
}
